import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import addSVG from '../icons/non-prefilled/16x16/add.svg';
import arrowLeftSVG from '../icons/non-prefilled/16x16/arrowLeft.svg';
import arrowRightSVG from '../icons/non-prefilled/16x16/arrowRight.svg';
import clearSVG from '../icons/non-prefilled/16x16/clear.svg';
import draftsSVG from '../icons/prefilled/16x16/drafts.svg';
import groupSVG from '../icons/prefilled/16x16/group.svg';
import homeSVG from '../icons/prefilled/16x16/home.svg';
import postSVG from '../icons/prefilled/16x16/post.svg';
import templateSVG from '../icons/prefilled/16x16/template.svg';
import topicSVG from '../icons/prefilled/16x16/topic.svg';
import cloudSVG from '../icons/non-prefilled/tabbar/cloud.svg';
import settingsSVG from '../icons/non-prefilled/16x16/settings.svg';
import type {
  DesktopClient,
  TabBarState,
  TabItem,
} from '../utils/desktopClient';
import { DesktopPlatforms } from '../utils/desktopClient';

import styles from './TabBarView.module.scss';

const iconCollection: { [key: string]: string } = {
  cloud: cloudSVG,
  home: homeSVG,
  post: postSVG,
  topic: topicSVG,
  template: templateSVG,
  drafts: draftsSVG,
  group: groupSVG,
  settings: settingsSVG,
};

interface TabBarViewProps {
  desktop?: DesktopClient;
}

const TabBarButton = (props: React.ComponentProps<typeof Button>) => (
  <Button {...props} className={classNames(styles.button, props.className)} />
);

const TabBarView = ({ desktop = window.desktop }: TabBarViewProps) => {
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [focusedTabId, setFocusedTabId] = useState(-1);
  const [canGoForward, setCanGoForward] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const isElectronWindows = desktop?.platform === DesktopPlatforms.windows;

  useEffect(() => {
    desktop?.onTabChange?.((state: TabBarState) => {
      setTabs(state.tabs);
      setFocusedTabId(state.focusedTabId);
      setCanGoForward(state.canGoForward);
      setCanGoBack(state.canGoBack);
    });
  }, [desktop]);

  useLayoutEffect(() => {
    // Falling back to addTab for backwards compatibility.
    if (desktop?.addInitialTab) {
      desktop?.addInitialTab(window.location.origin);
    } else {
      desktop?.addTab?.(window.location.origin);
    }
  }, [desktop]);

  const handleNewClick = () => {
    desktop?.addTab?.(window.location.origin);
  };

  const handleFocusClick = (id: number) => {
    desktop?.focusTab?.(id);
  };

  const handleCloseClick = (id: number) => {
    desktop?.closeTab?.(id);
  };

  const handleForwardClick = () => {
    desktop?.forwardInTab?.(focusedTabId);
  };

  const handleBackClick = () => {
    desktop?.backInTab?.(focusedTabId);
  };

  const handleDragStart = (index: number): void => {
    setIsDragging(true);
    if (tabs[index]) {
      // @ts-expect-error TODO - fix next time this file is edited
      desktop?.focusTab?.(tabs[index].id);
    }
  };

  const handleDragEnd = (
    sourceIndex: number,
    destinationIndex: number,
  ): void => {
    if (desktop?.moveTab) {
      // We have to move the tabs first in the React state to prevent flickering
      // of the tab bar.
      const element = tabs[sourceIndex];
      if (element) {
        tabs.splice(sourceIndex, 1);
        tabs.splice(destinationIndex, 0, element);
        setTabs(tabs);

        desktop.moveTab(sourceIndex, destinationIndex);
      }
    }
  };

  let previousToFocusedTabId = -1;
  for (let i = 0; i < tabs.length - 1; i += 1) {
    if (tabs[i + 1]?.id === focusedTabId) {
      // @ts-expect-error TODO - fix next time this file is edited
      previousToFocusedTabId = tabs[i].id;
      break;
    }
  }

  return (
    <div>
      <div
        className={classNames(styles.tabbar, {
          [styles.windows]: isElectronWindows,
        })}
      >
        <div className={styles.leftSideButtons}>
          {isElectronWindows && desktop?.toggleTitleBarMenu && (
            <div className={styles.menuButtonContainer}>
              <TabBarButton
                onClick={() => {
                  desktop?.toggleTitleBarMenu?.();
                }}
              >
                <svg
                  height="12px"
                  width="12px"
                  x="0px"
                  y="0px"
                  viewBox="0 0 10 7"
                >
                  <rect x="0" y="0" width="10" height="1" />
                  <rect x="0" y="3.5" width="10" height="1" />
                  <rect x="0" y="7" width="10" height="1" />
                </svg>
              </TabBarButton>
            </div>
          )}
          <TabBarButton
            className={styles.arrowLeft}
            disabled={!canGoBack}
            onClick={handleBackClick}
          >
            <SVGIcon size={16}>{arrowLeftSVG}</SVGIcon>
          </TabBarButton>

          <TabBarButton disabled={!canGoForward} onClick={handleForwardClick}>
            <SVGIcon size={16}>{arrowRightSVG}</SVGIcon>
          </TabBarButton>
        </div>

        <DragDropContext
          onDragStart={({ source }) => {
            handleDragStart(source.index);
          }}
          onDragEnd={({ source, destination }) => {
            setIsDragging(false);

            if (destination && destination.index !== source.index) {
              handleDragEnd(source.index, destination.index);
            }
          }}
        >
          <Droppable droppableId="droppable" direction="horizontal">
            {({ droppableProps, innerRef: droppableRef, placeholder }) => (
              <div
                className={styles.tabList}
                ref={droppableRef}
                {...droppableProps}
              >
                {tabs.map((tab: TabItem, index: number) => (
                  <Draggable
                    key={tab.id}
                    draggableId={`draggable-${tab.id}`}
                    index={index}
                  >
                    {(
                      {
                        draggableProps,
                        dragHandleProps,
                        innerRef: draggableRef,
                      },
                      snapshot,
                    ) => (
                      <div
                        key={`tabbar-${tab.id}`}
                        role="presentation"
                        className={classNames(styles.item, {
                          [styles.active]: tab.id === focusedTabId,
                          [styles.dragging]: snapshot.isDragging,
                        })}
                        onContextMenu={(event) => {
                          if (desktop?.openTabContextMenu) {
                            event.preventDefault();
                            desktop?.openTabContextMenu(tab.id);
                          }
                        }}
                        onMouseDown={() => handleFocusClick(tab.id)}
                        ref={draggableRef}
                        {...draggableProps}
                        {...dragHandleProps}
                      >
                        {tab.iconUrl !== '' ? (
                          <img
                            src={tab.iconUrl}
                            className={styles.iconImage}
                            alt=""
                          />
                        ) : (
                          <span className={styles.icon}>
                            <SVGIcon size={12}>
                              {iconCollection[tab.icon]}
                            </SVGIcon>
                          </span>
                        )}

                        <label className={styles.label}>{tab.title}</label>

                        {tabs.length > 1 && (
                          <div className={styles.closeButtonContainer}>
                            <TabBarButton
                              onClick={() => handleCloseClick(tab.id)}
                            >
                              <SVGIcon size={10}>{clearSVG}</SVGIcon>
                            </TabBarButton>
                          </div>
                        )}

                        {tab.id !== previousToFocusedTabId && (
                          <div className={styles.separator}>&nbsp;</div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}

                <div className={`${styles.item} ${styles.new}`}>
                  <div
                    className={classNames(styles.newTabButtonContainer, {
                      [styles.draggingOther]: isDragging,
                    })}
                  >
                    <TabBarButton onClick={handleNewClick}>
                      <SVGIcon size={12}>{addSVG}</SVGIcon>
                    </TabBarButton>
                  </div>
                </div>
                {placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default TabBarView;
